import { useEffect, useRef, type RefObject } from 'react';

/**
 * Dismiss-on-outside-click. While `enabled`, listens for a document mousedown
 * and calls `onOutside` when the click lands outside `ref`. Consolidates the
 * identical useEffect that was hand-rolled across ~10 components (popovers,
 * dropdowns, filter panels).
 *
 * The callback is held in a ref so the listener (re)subscribes only when
 * `enabled` flips — matching the original effects' `[]` / `[open]` dependency
 * arrays rather than re-subscribing on every render when callers pass an inline
 * arrow.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onOutside: () => void,
  enabled = true,
): void {
  const cbRef = useRef(onOutside);
  cbRef.current = onOutside;

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) cbRef.current();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [enabled, ref]);
}
